import React, { useState } from 'react'
import { CheckIcon, TagIcon, XIcon } from 'lucide-react'
import type { InboxFeedItem, InboxAssignee } from '../../types/inbox'
import type { ConversationLabel } from '../../api/conversationLabels'
import { LABEL_COLORS, createConversationLabel } from '../../api/conversationLabels'
import { addInboxLabel, removeInboxLabel, setPhoneCallLogState } from '../../api/inboxLabels'
import { AssigneePicker } from './AssigneePicker'
import { HandlingStatusSelect } from './HandlingStatusSelect'
import { InboxDetailSidebar } from './InboxDetailSidebar'
import { InboxEntityLinkToolbar } from './InboxEntityLinkToolbar'
import { LabelPickerPopover } from '../LabelPickerPopover'
import { assignInboxItem, setInboxHandlingStatus } from '../../api/inboxAssignment'
import { countOpenTasksForSource } from '../../api/inboxTasks'
import type { HandlingStatus } from '../../types/inbox'
import { getErrorMessage } from '../../lib/errors'
import { ConversationActionsMenu } from '../ConversationActionsMenu'
import type { ConversationState } from '../../api/conversationActions'
import { INBOX_ENTITY_LINK_FIELDS, type InboxEntityLinkKey } from '../../lib/inboxEntityLinks'
import { isEntityUnlinked } from '../../lib/inboxPermissions'

interface InboxCallDetailPanelProps {
  item: InboxFeedItem
  assignees: InboxAssignee[]
  canAssignOthers: boolean
  currentUserId: string | null
  currentUserRole: string | null
  labels: ConversationLabel[]
  labelIds: string[]
  onUpdated: () => void
  onLabelsChanged: () => void
  onCustomerLinked?: (customerId: string) => void
  authorName?: string | null
  onNotesChanged?: () => void
}

export function InboxCallDetailPanel({
  item,
  assignees,
  canAssignOthers,
  currentUserId,
  currentUserRole,
  labels,
  labelIds,
  onUpdated,
  onLabelsChanged,
  onCustomerLinked,
  authorName,
  onNotesChanged,
}: InboxCallDetailPanelProps) {
  const [labelPickerOpen, setLabelPickerOpen] = useState(false)
  const [resolveTaskWarning, setResolveTaskWarning] = useState<number | null>(null)
  const [pendingHandling, setPendingHandling] = useState<{
    status: HandlingStatus
    waitingUntil: string | null
  } | null>(null)

  const callLabels = labels.filter((l) => labelIds.includes(l.id))
  const callState = (item.state || 'active') as ConversationState

  async function handleAssign(userId: string | null) {
    try {
      await assignInboxItem({ itemType: 'phone_call_log', itemId: item.id, assigneeUserId: userId })
      onUpdated()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Assignment failed'))
    }
  }

  async function applyHandling(status: HandlingStatus, waitingUntil?: string | null) {
    try {
      await setInboxHandlingStatus({
        itemType: 'phone_call_log',
        itemId: item.id,
        handlingStatus: status,
        waitingUntil,
      })
      onUpdated()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to update status'))
    }
  }

  async function handleHandling(status: HandlingStatus, waitingUntil?: string | null) {
    if (status === 'resolved') {
      const count = await countOpenTasksForSource('phone_call_log', item.id)
      if (count > 0) {
        setResolveTaskWarning(count)
        setPendingHandling({ status, waitingUntil: waitingUntil ?? null })
        return
      }
    }
    await applyHandling(status, waitingUntil)
  }

  async function confirmResolveDespiteOpenTasks() {
    if (!pendingHandling) return
    await applyHandling(pendingHandling.status, pendingHandling.waitingUntil)
    setResolveTaskWarning(null)
    setPendingHandling(null)
  }

  async function toggleCallLabel(label: ConversationLabel, assigned: boolean) {
    try {
      if (assigned) await removeInboxLabel('phone_call_log', item.id, label.id)
      else await addInboxLabel('phone_call_log', item.id, label.id)
      onLabelsChanged()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to update label'))
    }
  }

  async function createAndAssignCallLabel(name: string) {
    try {
      const color = LABEL_COLORS[labels.length % LABEL_COLORS.length]
      const label = await createConversationLabel(name, color)
      await addInboxLabel('phone_call_log', item.id, label.id)
      onLabelsChanged()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to create label'))
    }
  }

  async function archiveCall() {
    try {
      await setPhoneCallLogState(item.id, 'archived')
      onUpdated()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to archive call'))
    }
  }

  const entityLinks: Partial<Record<InboxEntityLinkKey, string | null | undefined>> = {
    customer_id: item.customerId,
    lead_id: item.leadId,
    property_id: item.propertyId,
    contract_id: item.contractId,
    job_id: item.jobId,
    proposal_id: item.proposalId,
    employee_id: item.employeeId,
  }
  const activeEntityLinks = INBOX_ENTITY_LINK_FIELDS.filter((f) => entityLinks[f.key])

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {resolveTaskWarning !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Resolve with open tasks?</h3>
            <p className="text-sm text-gray-600">
              This call has {resolveTaskWarning} open task{resolveTaskWarning === 1 ? '' : 's'} still incomplete.
              You can resolve anyway — tasks will remain open.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setResolveTaskWarning(null)
                  setPendingHandling(null)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmResolveDespiteOpenTasks}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Resolve anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-shrink-0 border-b border-gray-200 bg-white relative">
        <div className="px-4 py-2.5 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 truncate">{item.contactName}</h2>
            <p className="text-xs text-gray-500 truncate">{item.contactInfo}</p>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <InboxEntityLinkToolbar
              itemType="phone_call_log"
              itemId={item.id}
              links={entityLinks}
              onUpdated={onUpdated}
              onCustomerLinked={onCustomerLinked}
              iconOnly
              pickerAsModal
            />
            <button
              type="button"
              onClick={() => setLabelPickerOpen((v) => !v)}
              title="Add or remove labels"
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              <TagIcon className="w-4 h-4" />
            </button>
            <AssigneePicker
              avatarOnly
              assignees={assignees}
              value={item.assignedToUserId}
              canAssignOthers={canAssignOthers}
              currentUserId={currentUserId}
              onChange={handleAssign}
            />
            <button
              type="button"
              onClick={() => handleHandling('resolved')}
              title="Mark resolved"
              className={`p-1.5 rounded-lg hover:bg-gray-100 ${
                item.handlingStatus === 'resolved' ? 'text-green-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <CheckIcon className="w-4 h-4" />
            </button>
            <ConversationActionsMenu
              compact
              state={callState}
              onAction={(action) => {
                if (action === 'archive') archiveCall()
              }}
              className="flex-shrink-0"
            />
          </div>
        </div>

        {(activeEntityLinks.length > 0 || callLabels.length > 0) && (
          <div className="px-4 pb-2 flex items-center gap-1.5 flex-wrap">
            {activeEntityLinks.map((f) => (
              <span
                key={f.key}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-700 text-[10px] rounded-full"
              >
                {f.label}
              </span>
            ))}
            {callLabels.map((label) => (
              <span
                key={label.id}
                className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-gray-100 text-gray-700 text-[11px] rounded-full"
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: label.color }} />
                {label.name}
                <button
                  type="button"
                  onClick={() => toggleCallLabel(label, true)}
                  className="text-gray-400 hover:text-red-600"
                  title={`Remove label "${label.name}"`}
                >
                  <XIcon className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="px-4 pb-2">
          <HandlingStatusSelect
            value={item.handlingStatus}
            waitingUntil={item.waitingUntil}
            onChange={handleHandling}
            compact
            hideBadge
          />
        </div>

        {isEntityUnlinked(item) && (
          <div className="flex items-center justify-between gap-2 border-t border-amber-100 bg-amber-50/60 px-4 py-1.5">
            <span className="text-[11px] text-amber-800">No CRM record linked to this call</span>
          </div>
        )}

        {labelPickerOpen && (
          <LabelPickerPopover
            labels={labels}
            selectedIds={new Set(labelIds)}
            onToggle={toggleCallLabel}
            onCreate={createAndAssignCallLabel}
            onClose={() => setLabelPickerOpen(false)}
            className="left-4 top-full mt-1"
          />
        )}
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden flex-col lg:flex-row">
        <div className="flex-1 min-w-0 min-h-0 overflow-y-auto px-4 py-4">
          <dl className="text-sm space-y-2">
            <div>
              <dt className="text-xs font-medium text-gray-500">Direction</dt>
              <dd className="text-gray-900">{item.direction === 'outbound' ? 'Outbound' : 'Incoming'}</dd>
            </div>
            {item.durationSeconds != null && item.durationSeconds > 0 && (
              <div>
                <dt className="text-xs font-medium text-gray-500">Duration</dt>
                <dd className="text-gray-900">
                  {Math.floor(item.durationSeconds / 60)}m {item.durationSeconds % 60}s
                </dd>
              </div>
            )}
          </dl>
        </div>
        <InboxDetailSidebar
          sourceItem={item}
          currentUserId={currentUserId}
          currentUserRole={currentUserRole}
          assigneeDefaultId={item.assignedToUserId || currentUserId}
          authorName={authorName}
          onNotesChanged={onNotesChanged}
          onTasksChanged={onUpdated}
        />
      </div>
    </div>
  )
}
